import { Box, Divider, Typography, useTheme, Theme } from '@mui/material';
import { Styles } from 'common/types/styles';
import { Fragment, ReactElement } from 'react';

export interface ActionsOption {
  name: string;
  value: string | number;
  icon?: React.ReactNode;
  available?: boolean;
  displayInMenu?: boolean;
}

const optionHeight = 50;
const iconSize = 10;
const textSize = 12;

const getStyles = (theme: Theme): Styles => ({
  container: { display: 'flex', flexDirection: 'column' },
  option: {
    height: optionHeight,
    fontSize: textSize,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: theme.palette.text.primary,
    borderRadius: 1,
    px: 1.5,
    transition: 'background-color 0.2s ease',
    '&:hover': {
      bgcolor: theme.palette.action.hover,
    },
  },
  icon: {
    width: iconSize,
    height: iconSize,
    marginRight: theme.spacing(1),
  },
  text: {
    marginLeft: 1,
    fontSize: 12,
    color: 'inherit',
  },
  divider: {
    borderColor: theme.palette.divider,
    mt: 0.5,
    mb: 0.5,
  },
  disabled: {
    color: theme.palette.text.disabled,
    cursor: 'not-allowed',
  },
});

interface Props {
  options: ActionsOption[];
  onClick: (value: string | number) => void;
}

export function ActionsContainer({ options, onClick }: Props): ReactElement {
  const theme = useTheme();
  const styles = getStyles(theme);

  return (
    <Box sx={styles.container}>
      {options.map((option, index) => (
        <Fragment key={option.value}>
          <Box
            sx={[
              styles.option,
              Object.prototype.hasOwnProperty.call(option, 'available') &&
                option.available === false &&
                styles.disabled,
            ].filter(Boolean)}
            onClick={() => {
              if (Object.prototype.hasOwnProperty.call(option, 'available') && option.available === false) return;
              onClick(option.value);
            }}
          >
            {option.icon}
            <Typography sx={styles.text}>{option.name}</Typography>
          </Box>
          {index !== options.length - 1 && <Divider sx={styles.divider} />}
        </Fragment>
      ))}
    </Box>
  );
}
